/**
 * The tonearm: a static base and gimbal under the pivot, an S-shaped tube that
 * swings with it, and a lift group that rotates about local Z to cue up and down.
 */

import * as THREE from 'three';
import { DIM } from '../../geometry/dims.ts';
import {
  ARM_EFF,
  ARM_OFF,
  armRestPoint,
  LIFT_ANGLE,
  liftRise,
  YAW_REST,
} from '../../geometry/tonearm.ts';
import type { DeckMaterials } from '../materials.ts';
import { cyl, hitVolume, roundedBoxGeometry } from '../shapes.ts';
import type { AddHit } from './hits.ts';

export interface Tonearm {
  /** Yawed to aim the stylus; owns the whole arm assembly. */
  pivot: THREE.Group;
  /** Rotated about Z to raise and lower the needle. */
  lift: THREE.Group;
  /** The stylus tip itself — its origin is the point that touches the record. */
  stylus: THREE.Mesh;
}

/** The arm tube's S-curve, in the pivot's own frame. */
const TUBE_PATH = [
  new THREE.Vector3(1.2, 0, 0),
  new THREE.Vector3(7.0, 0.05, 0.4),
  new THREE.Vector3(13.0, 0.05, -0.1),
  new THREE.Vector3(18.0, -0.05, -0.95),
  new THREE.Vector3(20.6, -0.25, -1.45),
];

/**
 * The headshell hangs from the tube, and the cartridge and stylus hang from
 * it. Every vertical figure here is measured from `DIM.arm.head`, which is
 * also where the pivot height comes from — so the tip lands on the record.
 */
function buildHeadshell(lift: THREE.Group, M: DeckMaterials): THREE.Mesh {
  const { head } = DIM.arm;
  const group = new THREE.Group();
  group.position.set(head.x, -head.drop, head.z);
  group.rotation.y = head.yaw;
  lift.add(group);

  const shell = new THREE.Mesh(roundedBoxGeometry(2.9, 1.9, 1.0, 0.3, 0.16, 8), M.shellWhite);
  shell.position.y = -0.5;
  shell.castShadow = true;
  group.add(shell);

  const fingerLift = new THREE.Mesh(
    roundedBoxGeometry(0.5, 1.2, 1.3, 0.2, 0.12, 6),
    M.accent.clone(),
  );
  fingerLift.material.emissiveIntensity = 0;
  fingerLift.position.set(1.5, -0.2, 0.2);
  fingerLift.rotation.z = 0.35;
  group.add(fingerLift);

  const cart = new THREE.Mesh(roundedBoxGeometry(2.0, 1.3, 0.85, 0.18, 0.1, 6), M.darkPlastic);
  cart.position.set(-0.15, -1.15, 0);
  group.add(cart);

  const cantilever = new THREE.Mesh(cyl(0.07, 0.07, 0.62, 10), M.chrome);
  cantilever.position.set(0.62, -1.55, 0);
  cantilever.rotation.z = 0.42;
  group.add(cantilever);

  /* The cone is shifted so its apex is the mesh origin: the tip's world
     position is then the point that meets the groove, nothing to add. */
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 10).translate(0, -0.15, 0), M.chrome);
  tip.position.set(head.tipX, -head.tipDrop, 0);
  tip.rotation.x = Math.PI;
  tip.name = 'stylus';
  group.add(tip);
  return tip;
}

function buildCounterweight(lift: THREE.Group, M: DeckMaterials): void {
  const stem = new THREE.Mesh(cyl(0.32, 0.32, 4.6, 20), M.aluminiumDark);
  stem.rotation.z = Math.PI / 2;
  stem.position.set(-2.4, 0, 0);
  lift.add(stem);

  const weight = new THREE.Mesh(cyl(1.45, 1.45, 2.3, 40), M.darkPlastic);
  weight.rotation.z = Math.PI / 2;
  weight.position.set(-4.0, 0, 0);
  weight.castShadow = true;
  lift.add(weight);

  const ring = new THREE.Mesh(cyl(1.5, 1.5, 0.35, 40), M.aluminium);
  ring.rotation.z = Math.PI / 2;
  ring.position.set(-2.9, 0, 0);
  lift.add(ring);
}

/**
 * The post the arm parks on, just outboard of the record. It reaches up to
 * the headshell as the arm sits lifted on it, so it rises with the pivot.
 */
function buildArmRest(root: THREE.Group, M: DeckMaterials): void {
  const inset = 1.4;
  const rest = armRestPoint(inset);
  const clipY = DIM.armY - 0.6 + liftRise(ARM_EFF - inset) - liftRise(ARM_EFF);
  const postH = clipY - DIM.top;

  const post = new THREE.Mesh(cyl(0.75, 0.9, postH, 24), M.shellWhite);
  post.position.set(rest.x, DIM.top + postH / 2, rest.z);
  post.castShadow = true;
  root.add(post);

  const clip = new THREE.Mesh(
    new THREE.TorusGeometry(0.78, 0.13, 10, 24, Math.PI),
    M.aluminiumDark,
  );
  clip.position.set(rest.x, clipY, rest.z);
  clip.rotation.x = -Math.PI / 2;
  clip.rotation.z = -rest.phi + Math.PI / 2;
  root.add(clip);
}

/** The cue lever, which raises and lowers the arm without touching it. */
function buildCueLever(root: THREE.Group, M: DeckMaterials, addHit: AddHit): void {
  const lever = new THREE.Group();
  lever.position.set(DIM.arm.x - 4.2, DIM.top + 1.0, DIM.arm.z + 2.4);
  root.add(lever);

  const base = new THREE.Mesh(roundedBoxGeometry(1.8, 2.6, 1.0, 0.4, 0.2, 8), M.shellWhite);
  lever.add(base);
  const stick = new THREE.Mesh(roundedBoxGeometry(0.7, 2.6, 0.7, 0.25, 0.15, 6), M.aluminiumDark);
  stick.position.set(0, 0.9, 0.5);
  lever.add(stick);

  const grab = hitVolume(new THREE.BoxGeometry(3.2, 3.6, 4.2));
  grab.position.copy(lever.position).add(new THREE.Vector3(0, 1.2, 0));
  root.add(addHit(grab, { type: 'cue' }));
}

export function buildTonearm(root: THREE.Group, M: DeckMaterials, addHit: AddHit): Tonearm {
  const pivot = new THREE.Group();
  pivot.position.set(DIM.arm.x, DIM.armY, DIM.arm.z);
  pivot.rotation.y = YAW_REST;
  root.add(pivot);

  /* The base column and collar stay under the pivot, unaffected by the lift. */
  const base = new THREE.Mesh(cyl(2.4, 2.7, DIM.arm.y - 0.4, 48), M.shellWhite);
  base.position.set(DIM.arm.x, DIM.top + (DIM.arm.y - 0.4) / 2, DIM.arm.z);
  base.castShadow = true;
  root.add(base);

  const collar = new THREE.Mesh(cyl(1.75, 1.95, 0.9, 40), M.aluminium);
  collar.position.set(DIM.arm.x, DIM.armY - 0.15, DIM.arm.z);
  root.add(collar);

  /* The deck opens with the arm parked and the needle up, so the lift starts
     raised — the state it is built to match says the arm is not down. */
  const lift = new THREE.Group();
  lift.rotation.z = LIFT_ANGLE;
  pivot.add(lift);

  const gimbal = new THREE.Mesh(roundedBoxGeometry(2.3, 2.3, 1.9, 0.5, 0.22, 8), M.aluminiumDark);
  gimbal.position.y = -0.5;
  lift.add(gimbal);

  const tube = new THREE.Mesh(
    new THREE.TubeGeometry(new THREE.CatmullRomCurve3(TUBE_PATH), 96, 0.33, 16, false),
    M.chrome,
  );
  tube.castShadow = true;
  lift.add(tube);

  buildCounterweight(lift, M);
  const stylus = buildHeadshell(lift, M);

  /* An invisible sleeve, so the arm is easy to grab on a touch screen. It runs
     from just clear of the gimbal to past the stylus, widening toward the
     headshell where a finger naturally lands, and it lies along the
     pivot–stylus line — so a grab at its centre aims the arm exactly where it
     already points. The wide end is the cylinder's bottom, which the two
     rotations carry to +x. */
  const along = 13.25;
  const grab = hitVolume(new THREE.CylinderGeometry(2.2, 3.2, 23.5, 12, 1, true));
  grab.rotation.set(0, -ARM_OFF, Math.PI / 2);
  grab.position.set(along * Math.cos(ARM_OFF), -0.4, along * Math.sin(ARM_OFF));
  lift.add(addHit(grab, { type: 'arm' }));

  buildArmRest(root, M);
  buildCueLever(root, M, addHit);

  return { pivot, lift, stylus };
}
